import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, Edit, MoreHorizontal, Trash2 } from "lucide-react"
import { Badge } from "./badge"
import { Button } from "./button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu"

// Generic action column creator
export function createActionColumn<T extends { id: string }>(
  actions: {
    onEdit?: (item: T) => void
    onDelete?: (item: T) => void
    onView?: (item: T) => void
    customActions?: Array<{
      label: string
      icon?: React.ComponentType<{ className?: string }>
      onClick: (item: T) => void
      className?: string
    }>
  },
  permissions?: {
    canEdit?: boolean
    canDelete?: boolean
    canView?: boolean
  }
): ColumnDef<T> {
  const { canEdit = true, canDelete = true, canView = true } = permissions || {}

  return {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const item = row.original

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.onView && canView && (
              <DropdownMenuItem onClick={() => actions.onView!(item)}>
                View
              </DropdownMenuItem>
            )}
            {actions.onEdit && canEdit && (
              <DropdownMenuItem onClick={() => actions.onEdit!(item)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {actions.customActions?.map((action, index) => (
              <DropdownMenuItem
                key={index}
                onClick={() => action.onClick(item)}
                className={action.className}
              >
                {action.icon && <action.icon className="mr-2 h-4 w-4" />}
                {action.label}
              </DropdownMenuItem>
            ))}
            {actions.onDelete && canDelete && (
              <DropdownMenuItem
                onClick={() => actions.onDelete!(item)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  }
}

// Generic sortable column creator
export function createSortableColumn<T>(
  accessorKey: string,
  header: string
): ColumnDef<T> {
  return {
    accessorKey,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-medium text-muted-foreground hover:text-foreground"
        >
          {header}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
  }
}

// Badge column creator for status/condition fields
export function createBadgeColumn<T>(
  accessorKey: string,
  header: string,
  options?: {
    getBadgeVariant?: (value: string) => "default" | "secondary" | "destructive" | "outline" | "warning";
    formatValue?: (value: string) => string;
  }
): ColumnDef<T> {
  return {
    accessorKey,
    header,
    cell: ({ row }) => {
      const value = row.getValue(accessorKey) as string
      const variant = options?.getBadgeVariant?.(value) || "secondary"
      const displayValue = options?.formatValue?.(value) || value

      return (
        <Badge variant={variant}>
          {displayValue}
        </Badge>
      )
    },
  }
}

// Date column creator
export function createDateColumn<T>(
  accessorKey: string,
  header: string,
  format: 'date' | 'datetime' = 'date'
): ColumnDef<T> {
  return {
    accessorKey,
    header,
    cell: ({ row }) => {
      const value = row.getValue(accessorKey) as string

      // Handle null, undefined, or empty values
      if (!value) {
        return <span className="text-muted-foreground">-</span>
      }

      const date = new Date(value)

      // Check if date is invalid
      if (isNaN(date.getTime())) {
        return <span className="text-muted-foreground">-</span>
      }

      if (format === 'datetime') {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${day}/${month}/${year} ${hours}:${minutes}`
      }

      return date.toLocaleDateString()
    },
  }
}

// Clickable link column creator
export function createLinkColumn<T>(
  accessorKey: string,
  header: string,
  onClick: (item: T) => void
): ColumnDef<T> {
  return {
    accessorKey,
    header,
    cell: ({ row }) => {
      const value = row.getValue(accessorKey) as string
      const item = row.original

      return (
        <button
          onClick={() => onClick(item)}
          className="text-sm font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {value}
        </button>
      )
    },
  }
}
